<template>
  <div class="device-content">
    <div class="page-header">
      <div>
        <h2 class="page-title">Polar 기기 관리</h2>
        <p class="page-subtitle">Polar BLE 기기를 등록하고 관리합니다.</p>
      </div>
      <div class="header-actions">
        <button class="primary-btn" @click="doScan" :disabled="scanning">
          {{ scanning ? "스캔 중..." : "BLE 스캔" }}
        </button>
        <button class="subtle-btn" @click="loadDevices">새로고침</button>
      </div>
    </div>

    <!-- 등록된 기기 목록 -->
    <section class="panel">
      <div class="panel-header">
        <h3>등록된 기기 ({{ devices.length }}대)</h3>
      </div>
      <div v-if="devices.length === 0" class="empty-box">
        등록된 기기가 없습니다.
      </div>
      <table v-else class="device-table">
        <thead>
          <tr>
            <th>번호</th>
            <th>이름</th>
            <th>BLE 식별자</th>
            <th>배정</th>
            <th>활성</th>
            <th>동작</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in devices" :key="d.device_no">
            <td>{{ d.device_no }}</td>
            <td>{{ d.label }}</td>
            <td class="identifier">{{ d.ble_identifier }}</td>
            <td>
              <span v-if="d.assigned_to" class="badge-assigned">{{
                d.assigned_to
              }}</span>
              <span v-else class="badge-free">-</span>
            </td>
            <td>
              <span :class="d.enabled ? 'badge-on' : 'badge-off'">{{
                d.enabled ? "활성" : "비활성"
              }}</span>
            </td>
            <td>
              <button class="icon-btn" @click="toggleDevice(d)">
                {{ d.enabled ? "비활성화" : "활성화" }}
              </button>
              <button class="icon-btn danger" @click="removeDevice(d)">
                삭제
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- 스캔 결과 -->
    <section v-if="scannedDevices.length > 0" class="panel">
      <div class="panel-header">
        <h3>스캔 결과 ({{ scannedDevices.length }}개)</h3>
      </div>
      <div class="scan-list">
        <div v-for="sd in scannedDevices" :key="sd.address" class="scan-item">
          <div class="scan-info">
            <div class="scan-name">{{ sd.name }}</div>
            <div class="scan-addr">{{ sd.address }}</div>
          </div>
          <div class="scan-actions">
            <input
              v-model.number="sd.newDeviceNo"
              type="number"
              placeholder="번호"
              class="num-input"
            />
            <input
              v-model="sd.newLabel"
              placeholder="이름"
              class="label-input"
            />
            <button
              class="primary-btn sm"
              @click="registerDevice(sd)"
              :disabled="!sd.newDeviceNo || !sd.newLabel"
            >
              등록
            </button>
          </div>
        </div>
      </div>
    </section>

    <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
    <p v-if="successMessage" class="success-text">{{ successMessage }}</p>
  </div>
</template>

<script>
import {
  getLocalDevices,
  addDevice,
  updateDevice,
  deleteDevice,
  scanDevices,
} from "@/lib/localHrApi";

export default {
  name: "DeviceManagementView",
  data() {
    return {
      devices: [],
      scannedDevices: [],
      scanning: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  mounted() {
    this.loadDevices();
  },
  methods: {
    async loadDevices() {
      try {
        const res = await getLocalDevices();
        this.devices = res.devices || [];
      } catch (e) {
        this.errorMessage = "기기 목록 로드 실패: " + e.message;
      }
    },
    async doScan() {
      this.scanning = true;
      this.errorMessage = "";
      this.scannedDevices = [];
      try {
        const res = await scanDevices();
        this.scannedDevices = (res.devices || []).map((d) => ({
          ...d,
          newDeviceNo: null,
          newLabel: d.name || "",
        }));
        if (!this.scannedDevices.length) {
          this.errorMessage = "주변에서 Polar 기기를 찾지 못했습니다.";
        }
      } catch (e) {
        this.errorMessage = "BLE 스캔 실패: " + e.message;
      }
      this.scanning = false;
    },
    async registerDevice(sd) {
      this.errorMessage = "";
      this.successMessage = "";
      try {
        await addDevice({
          device_no: sd.newDeviceNo,
          label: sd.newLabel,
          ble_identifier: sd.address,
          enabled: true,
        });
        this.successMessage = `${sd.newLabel} 등록 완료`;
        this.scannedDevices = this.scannedDevices.filter(
          (d) => d.address !== sd.address
        );
        await this.loadDevices();
      } catch (e) {
        this.errorMessage = "등록 실패: " + e.message;
      }
    },
    async toggleDevice(d) {
      try {
        await updateDevice(d.device_no, { enabled: !d.enabled });
        await this.loadDevices();
      } catch (e) {
        this.errorMessage = "상태 변경 실패: " + e.message;
      }
    },
    async removeDevice(d) {
      if (!confirm(`${d.label}을 삭제하시겠습니까?`)) return;
      try {
        await deleteDevice(d.device_no);
        await this.loadDevices();
      } catch (e) {
        this.errorMessage = "삭제 실패: " + e.message;
      }
    },
  },
};
</script>

<style scoped>
.device-content {
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  font-weight: 700;
}
.page-subtitle {
  font-size: 13px;
  color: #888;
  margin-top: 4px;
}
.header-actions {
  display: flex;
  gap: 8px;
}

.panel {
  background: #1e1e1e;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}
.panel-header {
  margin-bottom: 12px;
}
.panel-header h3 {
  font-size: 16px;
  font-weight: 600;
}

.primary-btn {
  background: #49de80;
  color: #000;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}
.primary-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.primary-btn.sm {
  padding: 6px 12px;
  font-size: 12px;
}
.subtle-btn {
  background: #333;
  color: #ccc;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.icon-btn {
  background: none;
  border: 1px solid #444;
  color: #ccc;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 4px;
}
.icon-btn.danger {
  border-color: #ef4444;
  color: #ef4444;
}
.icon-btn:hover {
  background: #333;
}

.empty-box {
  text-align: center;
  padding: 20px;
  color: #666;
}
.error-text {
  color: #ef4444;
  font-size: 13px;
  margin-top: 8px;
}
.success-text {
  color: #49de80;
  font-size: 13px;
  margin-top: 8px;
}
.badge-assigned {
  background: #1a3a1a;
  color: #49de80;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}
.badge-free {
  color: #555;
}

.device-table {
  width: 100%;
  border-collapse: collapse;
}
.device-table th,
.device-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #333;
  font-size: 13px;
}
.device-table th {
  color: #888;
  font-weight: 500;
}
.identifier {
  font-family: monospace;
  font-size: 11px;
  color: #888;
}
.badge-on {
  background: #1a3a1a;
  color: #49de80;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
}
.badge-off {
  background: #3a1a1a;
  color: #ef4444;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
}

.scan-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.scan-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #2a2a2a;
  border-radius: 8px;
}
.scan-info {
  flex: 1;
}
.scan-name {
  font-weight: 600;
  font-size: 14px;
}
.scan-addr {
  font-size: 11px;
  color: #888;
  font-family: monospace;
}
.scan-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}
.num-input {
  width: 60px;
  background: #333;
  color: #ccc;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
}
.label-input {
  width: 120px;
  background: #333;
  color: #ccc;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
}
</style>
